import { datadogRum } from '@datadog/browser-rum';
import { POST } from '@carvana/core-http';
import { getApiBaseUrl } from '@components/service-and-repair/services/base-url-config';

type OdometerReading = {
  odometerValue?: number | null;
  sourceDate?: string | null;
};

type VehicleMileageResponse = {
  data: {
    myCars: {
      vehicles: Array<{
        vehicle: {
          saleMileage?: number | null;
          customerReportedMileage?: Array<OdometerReading | null> | null;
        };
        saleEffectiveDate?: string | null;
      }>;
    };
  };
};

/**
 * SERV-405: Get latest VCM odometer reading post-purchase
 * Filters customerReportedMileage to only readings after saleEffectiveDate
 */
const getLatestVcmReading = (
  customerReportedMileage?: Array<OdometerReading | null> | null,
  saleEffectiveDate?: string | null
): { currentMileage: number; mileageLastUpdated: string } | null => {
  if (!saleEffectiveDate || !customerReportedMileage?.length) return null;

  const saleDate = new Date(saleEffectiveDate).getTime();
  const postPurchaseReadings = customerReportedMileage
    .filter((reading) => {
      if (!reading) return false;
      const readingDate = reading.sourceDate ? new Date(reading.sourceDate).getTime() : null;
      return readingDate && readingDate > saleDate && reading.odometerValue != null;
    })
    .sort((a, b) => {
      if (!a || !b) return 0;
      const dateA = a.sourceDate ? new Date(a.sourceDate).getTime() : 0;
      const dateB = b.sourceDate ? new Date(b.sourceDate).getTime() : 0;
      return dateB - dateA;
    });

  const latestReading = postPurchaseReadings?.[0];
  if (!latestReading || latestReading.odometerValue == null || !latestReading.sourceDate) {
    return null;
  }

  return {
    currentMileage: latestReading.odometerValue,
    mileageLastUpdated: latestReading.sourceDate,
  };
};

export const VehicleMileageService = {
  /**
   * SERV-405: Fetch vehicle mileage data from MyCars GraphQL API and calculate VCM-based fields
   * Returns purchaseMileage, currentMileage, and mileageLastUpdated for frontend usage
   */
  fetchVehicleMileageData: async (vehicleId: number): Promise<{
    purchaseMileage: number | null;
    currentMileage: number | null;
    mileageLastUpdated: string | null;
  } | null> => {
    const ddAction = 'fetch-vehicle-mileage-data';
    datadogRum.addAction(ddAction, { type: 'start', vehicleId });

    // OP uses the MyCars GraphQL API to interface with GraphQL
    const graphqlUrl = `${getApiBaseUrl()}/core/mycarsgraphql/api/v1/graphql/`;

    try {
      const response = await POST(
        graphqlUrl,
        {
          query: `
            query GetVehicleMileage($vehicleId: Long) {
              myCars(vehicleId: $vehicleId) {
                vehicles {
                  vehicle {
                    saleMileage
                    customerReportedMileage {
                      odometerValue
                      sourceDate
                    }
                  }
                  saleEffectiveDate
                }
              }
            }
          `,
          variables: { vehicleId },
        },
        {
          headers: {
            Accept: '*/*',
            'apollographql-client-name': 'mycars-op',
            'apollographql-client-version': '1.0',
            'Content-Type': 'application/json',
          },
          authorized: true,
        }
      );

      const data: VehicleMileageResponse = await response.json();

      if (!response.ok) {
        const { message } = (data as any) || {};
        datadogRum.addAction(ddAction, { type: 'error', error: message, status: response.status });
        return null;
      }

      const vehicleData = data?.data?.myCars?.vehicles?.[0];
      if (!vehicleData) {
        datadogRum.addAction(ddAction, { type: 'error', error: 'No vehicle found' });
        return null;
      }

      const { saleMileage, customerReportedMileage } = vehicleData.vehicle;
      const { saleEffectiveDate } = vehicleData;

      // SERV-405: Get latest VCM reading for "last known mileage" display
      const vcmReading = getLatestVcmReading(customerReportedMileage, saleEffectiveDate);

      datadogRum.addAction(ddAction, {
        type: 'success',
        hasVcmData: !!vcmReading,
        purchaseMileage: saleMileage,
        currentMileage: vcmReading?.currentMileage,
      });

      return {
        purchaseMileage: saleMileage ?? null,
        currentMileage: vcmReading?.currentMileage ?? null,
        mileageLastUpdated: vcmReading?.mileageLastUpdated ?? null,
      };
    } catch (e) {
      datadogRum.addAction(ddAction, { type: 'error', error: e });
      return null;
    }
  },
};

export default VehicleMileageService;
